// Oppslag mot foretaksregisteret (Enhetsregisteret i Norge). Reglene for *naar*
// et oppslag skal skje ligger her, ikke i UI-et, slik at de kan testes uten DOM.
//
// Viktig premiss: oppslaget er alltid et forslag. Kunder og leverandorer kan
// vaere privatpersoner uten organisasjonsnummer, og da skal skjemaet fungere
// akkurat som for — ingen treff, ingen sperre, ingen krav om org.nr.
(function initBusinessRegistry(global) {
  const text = (value) => String(value || '').trim();
  const digits = (value) => text(value).replace(/\D/g, '');

  // «123 456 789» og «987654321» er org.nr.-input. «Kari Nordmann» er det ikke,
  // selv om navnet skulle inneholde et tall.
  const looksLikeOrgNumber = (value) => {
    const raw = text(value);
    return Boolean(digits(raw)) && /^[\s\d.-]+$/.test(raw);
  };

  const country = () => String(
    global.PlummeBackend?.appState?.settings?.country
    || global.PlummeBackend?.getCompany?.()?.country
    || 'NO',
  ).toUpperCase();

  const formatAddress = (match) => [
    text(match?.address),
    [text(match?.postalCode), text(match?.postalPlace)].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');

  // Hva skal panelet gjore for denne inputen?
  //   off      - landet har ingen registerkobling
  //   idle     - ingenting aa slaa opp (for kort, eller allerede brukt treff)
  //   wait-org - brukeren har begynt paa et org.nr., men ikke skrevet ni siffer
  //   search   - slaa opp `query`
  const plan = (value, options = {}) => {
    if (String(options.country || country()).toUpperCase() !== 'NO') return { action: 'off', query: '' };
    const raw = text(value);
    const org = digits(raw);
    if (org.length === 9 && org === digits(options.dismissedOrg)) return { action: 'idle', query: '' };
    if (looksLikeOrgNumber(raw) && org.length < 9) return { action: 'wait-org', query: '' };
    const lookup = org.length === 9 ? org : raw;
    if (lookup.length < 2) return { action: 'idle', query: '' };
    return { action: 'search', query: lookup };
  };

  const fieldsFromMatch = (match) => ({
    name: text(match?.businessName || match?.name),
    org: text(match?.organizationNumber || match?.orgnr),
    email: text(match?.email),
    phone: text(match?.phone),
    address: text(match?.address),
    zip: text(match?.postalCode),
    city: text(match?.postalPlace),
    addressLine: formatAddress(match),
  });

  // Fyller ut et kundeutkast. Identitetsfeltene (navn, org.nr., adresse) kommer
  // fra registeret — det er derfor brukeren valgte treffet. E-post og telefon
  // gjor det ikke: registerets adresse er sjelden den fakturaen skal til, og en
  // adresse brukeren allerede har skrevet skal aldri byttes ut i det stille.
  const applyToCustomerDraft = (match, current = {}) => {
    const fields = fieldsFromMatch(match);
    return {
      ...current,
      name: fields.name || current.name || '',
      org: fields.org || current.org || '',
      email: current.email || fields.email || '',
      phone: current.phone || fields.phone || '',
      addr: fields.address || current.addr || '',
      zip: fields.zip || current.zip || '',
      city: fields.city || current.city || '',
    };
  };

  // Av/paa-valget for registersoket huskes i nettleseren. En bruker med mest
  // privatkunder skal slaa det av en gang, ikke i hvert eneste kundeskjema.
  const PREFERENCE_KEY = 'plumme.registryLookup';

  const lookupEnabled = () => {
    try {
      return global.localStorage?.getItem(PREFERENCE_KEY) !== 'off';
    } catch (error) {
      return true;
    }
  };

  const setLookupEnabled = (enabled) => {
    try {
      global.localStorage?.setItem(PREFERENCE_KEY, enabled ? 'on' : 'off');
    } catch (error) {
      // Privat nettleservindu uten lagring: valget gjelder da bare denne okten.
    }
    return Boolean(enabled);
  };

  const NORWAY_COMPANY_TYPES = [
    ['AS', 'Aksjeselskap (AS)'],
    ['ASA', 'Allmennaksjeselskap (ASA)'],
    ['ENK', 'Enkeltpersonforetak (ENK)'],
    ['ANS', 'Ansvarlig selskap (ANS)'],
    ['DA', 'Ansvarlig selskap med delt ansvar (DA)'],
    ['NUF', 'Norskregistrert utenlandsk foretak (NUF)'],
    ['SA', 'Samvirkeforetak (SA)'],
    ['BA', 'Selskap med begrenset ansvar (BA)'],
    ['KS', 'Kommandittselskap (KS)'],
    ['STI', 'Stiftelse (STI)'],
    ['FLI', 'Forening/lag/innretning (FLI)'],
    ['BRL', 'Borettslag (BRL)'],
    ['ESEK', 'Eierseksjonssameie (ESEK)'],
    ['IKS', 'Interkommunalt selskap (IKS)'],
    ['KF', 'Kommunalt foretak (KF)'],
    ['FKF', 'Fylkeskommunalt foretak (FKF)'],
    ['KBO', 'Konkursbo (KBO)'],
    ['PRE', 'Pensjonskasse (PRE)'],
    ['TVAM', 'Tvangsregistrert for MVA (TVAM)'],
    ['ANNA', 'Annen juridisk person (ANNA)'],
  ];
  function companyTypeOptions(data) {
    const code = String(data.companyTypeCode || '').toUpperCase();
    const label = data.companyTypeLabel || code;
    const options = String(data.country || '').toUpperCase() === 'NO' ? [...NORWAY_COMPANY_TYPES] : [];
    if (code && !options.some(([value]) => value === code)) options.unshift([code, label ? `${label} (${code})` : code]);
    return options;
  }
  global.PlummeBusinessRegistry = {
    companyTypeOptions,
    country,
    lookupEnabled,
    setLookupEnabled,
    formatAddress,
    looksLikeOrgNumber,
    plan,
    fieldsFromMatch,
    applyToCustomerDraft,
  };
})(typeof window !== 'undefined' ? window : globalThis);

(function initSupplierRecords(global) {
  const text = (value) => String(value || '').trim();
  const countryCode = (value) => text(value).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
  const businessCountry = () => countryCode(global.PlummeBackend?.appState?.settings?.country || global.PlummeI18n?.businessCountry?.() || 'NO') || 'NO';
  const supplierCountry = (supplier, fallbackCountry = businessCountry()) => countryCode(supplier?.country) || countryCode(fallbackCountry);
  const isForeign = (supplier, accountingCountry = businessCountry()) => {
    const local = countryCode(accountingCountry), supplierCode = countryCode(supplier?.country);
    return Boolean(local && supplierCode && supplierCode !== local);
  };
  const formDraft = (supplier = {}, accountingCountry = businessCountry()) => ({
    ...supplier, country: supplierCountry(supplier, accountingCountry),
    registrationNumber: text(supplier.registrationNumber), vatNumber: text(supplier.vatNumber),
  });
  const withForeignStatus = (draft, foreign, accountingCountry = businessCountry()) => ({
    ...draft, country: foreign ? (isForeign(draft, accountingCountry) ? countryCode(draft.country) : '') : countryCode(accountingCountry),
  });
  // Country alone cannot prove import or reverse-charge treatment. New lines
  // therefore start without a VAT code and remain fully editable by the user.
  const purchaseLinesWithoutVatCode = (lines) => (Array.isArray(lines) ? lines : []).map((line) => ({
    ...line, vat: 0, vatRate: 0, vatTreatment: 'zeroRated', vatSelection: 'rate:0',
    purchaseVatCode: '', standardVatCode: '', reverse: 'nei', reverseChargeCode: '',
  }));
  const purchaseLinesForSupplierSelection = (lines, supplier, accountingCountry = businessCountry()) => (
    isForeign(supplier, accountingCountry) ? purchaseLinesWithoutVatCode(lines) : lines
  );
  global.PlummeSupplierRecords = {
    businessCountry, countryCode, supplierCountry, isForeign, formDraft, withForeignStatus,
    purchaseLinesWithoutVatCode, purchaseLinesForSupplierSelection,
  };
})(typeof window !== 'undefined' ? window : globalThis);
